import {
  PhoneOff,
  PhoneMissed,
  Voicemail,
  ArrowRightLeft,
  Clock,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import type { CallEndReason } from '@vagusx/clients';

/**
 * Single source of truth for how each call-end reason is presented — label
 * (shared by the transcript's `CallEndedRow` and the plain-text download in
 * `buildTranscriptText`) and icon (used only by `CallEndedRow`). One
 * `Record<CallEndReason, …>` so a new reason forces both fields in one place
 * instead of drifting across two parallel maps. Kept out of the API payload so
 * the copy lives here and stays localizable.
 */
export const CALL_END_REASON_META: Record<CallEndReason, { label: string; icon: LucideIcon }> = {
  patient_hung_up: { label: 'Patient hung up', icon: PhoneOff },
  assistant_ended: { label: 'Ended by assistant', icon: PhoneOff },
  voicemail: { label: 'Reached voicemail', icon: Voicemail },
  not_reachable: { label: 'Line not reachable', icon: PhoneMissed },
  transferred: { label: 'Transferred', icon: ArrowRightLeft },
  max_duration: { label: 'Reached max call length', icon: Clock },
  error: { label: 'Ended unexpectedly', icon: TriangleAlert },
};

/** Label for a reason, falling back to a generic marker for unknown values. */
export function callEndReasonLabel(reason: string): string {
  return CALL_END_REASON_META[reason as CallEndReason]?.label ?? 'Call ended';
}

/** Icon for a reason, falling back to a generic phone-off glyph. */
export function callEndReasonIcon(reason: string): LucideIcon {
  return CALL_END_REASON_META[reason as CallEndReason]?.icon ?? PhoneOff;
}
